// FILE: src/services/badgeService.js
// All badge SELECTION logic for the home-page badge system. Pure functions over
// the data in src/config/badges.js — no React, no I/O, easy to unit test.
//
// Responsibilities (per spec):
//   • determine eligible badges (by type, cert target, active flag)
//   • enforce the max-badge rules (≤1 product + ≤1 telemetry per card)
//   • rotation logic for the platform row
//   • telemetry filtering (a badge must be EARNED via BADGE_TRIGGER_RULES)
//   • prioritization (higher `priority` wins a contested slot)
//
// Telemetry input shape (future-ready). Callers pass `telemetryByCert`, a map of
// examId → snapshot. A snapshot is plain data the telemetry layer will populate
// later (see src/services/telemetryService.js). Recognized fields:
//   {
//     sessionsGrowthPct: number,  // % change in sessions over the rule window
//     avgRating:         number,  // mean learner rating (e.g. 0–5)
//     sessionsRank:      number,  // popularity rank across certs (1 = most used)
//     sampleCount:       number,  // n behind the snapshot (gates minSamples)
//   }
// Until that data flows, callers pass {} (or omit it) and NO telemetry badge
// renders — which is exactly right: social proof is earned, never hardcoded.

import {
  BADGE_TYPES,
  BADGE_TRIGGER_RULES,
  BADGE_ROTATION_CONFIG,
  CARD_BADGE_MODE,
  getActiveBadges,
} from '../config/badges';

// ── Helpers ──────────────────────────────────────────────────────────────────

const byPriorityDesc = (a, b) => (b.priority || 0) - (a.priority || 0);

/**
 * Does this badge target the given certification?
 * '*' (or an array containing '*') matches every cert.
 * @param {object} badge
 * @param {string} certId
 * @returns {boolean}
 */
export function targetsCert(badge, certId) {
  const t = badge && badge.certificationTargets;
  if (!t) return false;
  if (t === '*') return true;
  if (Array.isArray(t)) return t.includes('*') || t.includes(certId);
  return t === certId;
}

const OPS = {
  gte: (a, b) => a >= b,
  gt: (a, b) => a > b,
  lte: (a, b) => a <= b,
  lt: (a, b) => a < b,
  eq: (a, b) => a === b,
};

/**
 * Evaluate a telemetry trigger against a snapshot.
 * @param {string} triggerId  key into BADGE_TRIGGER_RULES
 * @param {object} snapshot   per-cert telemetry data (see file header)
 * @returns {boolean} true only when the rule is satisfied AND any minSamples
 *                    guard is cleared. Missing rule / snapshot / metric → false.
 */
export function evaluateTrigger(triggerId, snapshot) {
  const rule = BADGE_TRIGGER_RULES[triggerId];
  if (!rule || !snapshot) return false;
  if (rule.minSamples != null && (snapshot.sampleCount || 0) < rule.minSamples) {
    return false;
  }
  const metricVal = snapshot[rule.metric];
  if (metricVal == null) return false;
  const op = OPS[rule.op];
  if (!op) return false;
  return op(metricVal, rule.value);
}

// ── Product badges ───────────────────────────────────────────────────────────

/**
 * The single product badge for a cert (max 1 — highest priority wins).
 * @param {string} certId
 * @returns {object|null}
 */
export function getProductBadge(certId) {
  const candidates = getActiveBadges()
    .filter((b) => b.type === BADGE_TYPES.PRODUCT && targetsCert(b, certId))
    .sort(byPriorityDesc);
  return candidates[0] || null;
}

// ── Telemetry badges ─────────────────────────────────────────────────────────

/**
 * Earned telemetry badges for a cert, highest priority first. Empty when no
 * snapshot is supplied or no trigger fires (the common case today).
 * @param {string} certId
 * @param {object} telemetryByCert  map examId → snapshot
 * @returns {object[]}
 */
export function getEligibleTelemetryBadges(certId, telemetryByCert = {}) {
  const snapshot = telemetryByCert[certId];
  if (!snapshot) return [];
  return getActiveBadges()
    .filter((b) => b.type === BADGE_TYPES.TELEMETRY && targetsCert(b, certId))
    .filter((b) => evaluateTrigger(b.triggerCondition, snapshot))
    .sort(byPriorityDesc);
}

// ── Card composition (enforces the max-badge + placement rules) ───────────────

/**
 * Resolve the badges shown ON a single cert card, honoring the canonical UI
 * rules: never stack, ≤1 product, ≤1 telemetry.
 *
 * @param {string} certId
 * @param {object} [opts]
 * @param {object} [opts.telemetryByCert={}] map examId → telemetry snapshot
 * @param {string} [opts.mode=CARD_BADGE_MODE] 'dual' | 'single'
 * @param {object|null} [opts.productOverride] admin-managed product badge:
 *        an object = use it; `null` = explicit "no emblem"; `undefined`
 *        (omitted) = fall back to the static catalog (getProductBadge).
 * @returns {{product: object|null, telemetry: object|null, mode: string}}
 *
 *   'dual'   → product (top-right) and telemetry (top-left) may both appear.
 *   'single' → one slot, top-right, priority Product > Telemetry.
 */
export function selectCardBadges(certId, opts = {}) {
  const { telemetryByCert = {}, mode = CARD_BADGE_MODE, productOverride } = opts;
  // Admin DB config wins when provided (object or explicit null); otherwise the
  // static catalog decides.
  const product =
    productOverride !== undefined ? productOverride : getProductBadge(certId);
  const telemetry = getEligibleTelemetryBadges(certId, telemetryByCert)[0] || null;

  if (mode === 'single') {
    // One slot: product outranks telemetry; telemetry only shows when no product.
    if (product) return { product, telemetry: null, mode };
    return { product: null, telemetry, mode };
  }
  return { product, telemetry, mode };
}

// ── Platform row (rotating) ───────────────────────────────────────────────────

/**
 * Active platform badges eligible for the rotating row, highest priority first.
 * @param {string} [certId='*'] limit to a cert (defaults to all)
 * @returns {object[]}
 */
export function getPlatformBadges(certId = '*') {
  return getActiveBadges()
    .filter(
      (b) =>
        b.type === BADGE_TYPES.PLATFORM &&
        (certId === '*' || targetsCert(b, certId))
    )
    .sort(byPriorityDesc);
}

/**
 * Which platform badge(s) are visible at a given rotation tick. Wraps around the
 * list so any tick value is valid. Honors `visibleCount` (max 1 by config).
 * @param {object[]} badges       ordered platform badges
 * @param {number}   tick         monotonically increasing rotation counter
 * @param {number}   [visibleCount=BADGE_ROTATION_CONFIG.visibleCount]
 * @returns {object[]}
 */
export function getVisiblePlatformBadges(
  badges,
  tick = 0,
  visibleCount = BADGE_ROTATION_CONFIG.visibleCount
) {
  if (!Array.isArray(badges) || badges.length === 0) return [];
  const n = badges.length;
  const count = Math.max(1, Math.min(visibleCount, n));
  const start = ((tick % n) + n) % n; // safe modulo for negative ticks
  const out = [];
  for (let i = 0; i < count; i++) out.push(badges[(start + i) % n]);
  return out;
}
